<template>
  <div class="page-table-layout accruals-estimates-page" data-testid="accruals-estimates-page">
    <v-card
      class="all-width flex-card shadow-off"
      :loading="pageLoading && !initialLoading"
      :disabled="pageLoading && !initialLoading"
      loader-height="2"
    >
      <v-card-text class="page-table-layout__body accruals-estimates-page__body">
        <div class="accruals-estimates-page__header-block">
          <header class="accruals-estimates-page__header">
            <div class="accruals-estimates-page__title-stack">
              <div class="accruals-estimates-page__title-group">
                <h1 class="accruals-estimates-page__title">Accruals</h1>
                <v-tooltip bottom content-class="tooltip-bottom">
                  <template #activator="{ on, attrs }">
                    <button
                      type="button"
                      class="accruals-estimates-page__refresh-icon-btn"
                      v-bind="attrs"
                      :disabled="headerRefreshLoading"
                      aria-label="Refresh estimates"
                      data-testid="accruals-estimates-page__refresh-btn"
                      v-on="on"
                      @click="onHeaderRefresh"
                    >
                      <img
                        :src="assets.ICON_RELOAD"
                        alt=""
                        class="accruals-estimates-page__refresh-icon"
                        :class="{ 'accruals-estimates-page__refresh-icon--spin': headerRefreshLoading }"
                        aria-hidden="true"
                      />
                    </button>
                  </template>
                  <span>Refresh</span>
                </v-tooltip>
              </div>
              <p class="accruals-estimates-page__subtitle">
                Your workspace for reconciling accruals for the current close cycle
              </p>
            </div>

            <div class="accruals-estimates-page__header-actions">
              <accruals-estimates-view-switcher
                :value="activeTab"
                :tabs="tabs"
                :disabled="pageLoading"
                test-id-prefix="accruals-estimates-page"
                @input="onTabChange"
              />
              <accruals-estimates-overflow-menu
                :items="headerMenuItems"
                aria-label="More actions"
                activator-testid="accruals-estimates-page__header-overflow-btn"
                menu-testid="accruals-estimates-page__header-overflow-menu"
                @select="onHeaderMenuSelect"
              />
            </div>
          </header>

          <div
            v-if="activeTab === 'estimates'"
            class="accruals-estimates-page__toolbar accruals-estimates-page__status-bar"
          >
            <pipeline-status-chips
              class="accruals-estimates-page__status-chips"
              :loading="false"
              :metadata="{ _id: projectId }"
              :chips="estimatesStatusChips"
              :status-variant-map="estimatesStatusVariantMap"
              :selected-tab-stage="activeFilter"
              :active-summary="estimatesStatusSummary"
              separator-mode="divider-before"
              divider-before-key="rejected"
              :show-when-empty="true"
              data-testid="accruals-estimates-page__filters"
              @select-stage="onEstimatesStatusSelect"
            />

            <div class="accruals-estimates-page__toolbar-actions">
              <v-btn
                v-if="hasActiveTableFilter"
                text
                class="data-table-action__clear-filter-btn"
                data-testid="accruals-estimates-page__clear-filter-btn"
                @click="onClearColumnFilter"
              >
                <span>Clear Filter</span>
              </v-btn>
              <base-button
                v-if="canRunEstimation"
                secondary
                small
                class="shadow-off"
                :image="assets.ICON_RERUN"
                image-right
                :image-width="16"
                image-alt=""
                :loading="rerunLoading"
                data-testid="accruals-estimates-page__rerun-btn"
                @click="onReRunEstimate"
              >
                Re Run
              </base-button>
              <v-tooltip bottom content-class="tooltip-bottom">
                <template #activator="{ on, attrs }">
                  <base-button
                    v-bind="attrs"
                    secondary
                    icon-style
                    small
                    class="shadow-off"
                    :image="assets.ESTIMATES_UPLOAD"
                    :image-width="16"
                    image-alt=""
                    :loading="prepareScheduleLoading"
                    :disabled="prepareScheduleLoading"
                    aria-label="Bulk GL upload"
                    data-testid="accruals-estimates-page__upload-btn"
                    v-on="on"
                    @click="onBulkGlUpload"
                  />
                </template>
                <span>Bulk GL upload</span>
              </v-tooltip>
              <base-button
                primary
                small
                class="shadow-off"
                :image="assets.ESTIMATES_ADD_WHITE"
                :image-width="16"
                image-alt=""
                data-testid="accruals-estimates-page__add-accruals-btn"
                @click="onAddAccruals"
              >
                New estimate
              </base-button>
              <!-- <accruals-estimates-overflow-menu
                :items="toolbarMenuItems"
                aria-label="More toolbar actions"
                activator-testid="accruals-estimates-page__toolbar-overflow-btn"
                menu-testid="accruals-estimates-page__toolbar-overflow-menu"
                @select="onToolbarMenuSelect"
              /> -->
            </div>
          </div>
        </div>

        <div
          v-if="snapshotStale"
          class="accruals-estimates-page__stale-banner"
          role="status"
          data-testid="accruals-estimates-page__stale-banner"
        >
          Snapshot data was updated after the last estimate run. Amounts on screen may be stale.
          Re Run Estimate will revert both the Inference estimate and the Computation Rule estimate
          for the affected population.
        </div>

        <template v-if="activeTab === 'estimates'">

          <p
            v-if="!tableLoading && !allRows.length && emptyNotice"
            class="accruals-estimates-page__access-notice"
            data-testid="accruals-estimates-page__access-notice"
          >
            {{ emptyNotice }}
          </p>
          <div
            v-else-if="tableLoading"
            class="accruals-estimates-page__table-area flex-fill-table"
            data-testid="accruals-estimates-page__table-loading"
          >
            <accruals-estimates-loader />
          </div>
          <div
            v-else-if="filterEmptyMessage"
            class="accruals-estimates-page__table-area"
          >
            <div
              class="accruals-estimates-page__filter-empty"
              data-testid="accruals-estimates-page__filter-empty"
            >
              <p class="accruals-estimates-page__filter-empty-title">{{ filterEmptyMessage }}</p>
              <p
                v-if="filterEmptyHint"
                class="accruals-estimates-page__filter-empty-sub"
              >{{ filterEmptyHint }}</p>
            </div>
          </div>
          <div v-else class="accruals-estimates-page__table-area flex-fill-table">
            <div class="accruals-estimates-page__content">
              <accruals-estimates-table
                ref="estimatesTable"
                :key="activeFilter"
                :rows="tableRows"
                :filter-options-data="vendorGroups"
                :selected-ids.sync="selectedIds"
                :selected-row-id="selectedRow?._id"
                :expanded-group-ids="expandedGroupIds"
                :source-by-group-id="sourceByGroupId"
                :confirming-group-id="confirmingGroupId"
                :saving-group-id="savingGroupId"
                :can-review="canReviewEstimates"
                :review-location-keys="reviewLocationKeys"
                :approve-location-keys="approveLocationKeys"
                :role="workbenchRole"
                :can-approve="canApproveEstimates"
                :items-per-page="itemsPerPage"
                @toggle-group="onToggleGroup"
                @source-change="onSourceChange"
                @open-split-view="onOpenSplitView"
                @row-menu="onRowMenu"
                @confirm="onConfirmGroup"
                @approve="onApproveEstimate"
                @reject="onRejectEstimate"
                @save-gl="onSaveGl"
                @follow-up="onFollowUp"
                @filter-item="onColumnFilter"
                @clear-filter="onClearColumnFilterState"
                @filter-active-change="onFilterActiveChange"
              />

              <accruals-estimates-bulk-bar
                v-if="selectedIds.length > 0 && isApproverRole"
                :selected-count="selectedEstimateLineCount"
                :loading="bulkActionLoading"
                @deselect-all="onClearBulkSelection"
                @reject="onBulkAction('reject')"
                @approve-all="onOpenBulkApproveDialog"
              />
              <bulk-selection-bar
                v-else-if="selectedIds.length > 0"
                :selected-count="selectedIds.length"
                :actions="bulkSelectionActions"
                :loading="bulkActionLoading"
                test-id-prefix="accruals-estimates-bulk-action-bar"
                aria-label="Bulk estimate actions"
                @clear-selection="onClearBulkSelection"
                @action="onBulkAction"
              />

              <accruals-estimate-detail-panel
                :visible="detailPanelOpen"
                :row="selectedRow"
                :can-review="canReviewSelectedRow"
                :can-unlock="canUnlockEstimates"
                :role="detailRole"
                :can-approve="canApproveSelectedRow"
                :project-id="projectId"
                :saving="Boolean(savingGroupId)"
                :status-updating="statusUpdating"
                :detail-refresh-key="detailRefreshKey"
                @close="closeDetailPanel"
                @line-updated="applyEstimateLine"
                @refresh-settled="statusUpdating = false"
                @approve-estimate="onApproveEstimate"
                @submit="onSubmitEstimate"
                @reject-estimate="onRejectEstimate"
                @request-resubmission="onRequestResubmission"
                @unlock-estimate="onUnlockEstimate"
                @save-gl="onSaveGl"
                @exclude-from-cycle="onExcludeFromCycle"
                @include-in-cycle="onIncludeInCycle"
                @view-info-request="onViewInfoRequest"
              />
            </div>
          </div>

          <accruals-estimates-confirm-dialog
            v-model="bulkApproveOpen"
            :heading="bulkApproveHeading"
            :intro="bulkApproveCopy.INTRO"
            :items="bulkApproveItems"
            :note.sync="bulkApproveNote"
            :note-label="bulkApproveCopy.NOTE_LABEL"
            :note-placeholder="bulkApproveCopy.NOTE_PLACEHOLDER"
            :cancel-label="bulkApproveCopy.CANCEL_LABEL"
            :confirm-label="bulkApproveCopy.CONFIRM_LABEL"
            :loading="bulkActionLoading"
            test-id-prefix="accruals-estimates-page__approve-dialog"
            @confirm="onConfirmBulkApprove"
          />

          <lb-dialog
            :value="bulkReasonOpen"
            :heading="bulkReasonMode === 'exclude' ? 'Exclude from cycle' : 'Reject estimates'"
            data-testid="accruals-estimates-page__bulk-reject-dialog"
            @input="bulkReasonOpen = $event"
            @change="bulkReasonOpen = $event"
          >
            <template #body>
              <div class="accruals-estimates-page__bulk-reason-body">
                <p class="accruals-estimates-page__bulk-reason-refs">
                  {{ bulkReasonRefs }}
                </p>
                <label
                  class="accruals-estimates-page__bulk-reason-label"
                  for="accruals-estimates-page__bulk-reason-input"
                >
                  {{ bulkReasonMode === 'exclude' ? 'Remarks*' : 'Reason (shared with the preparer)' }}
                </label>
                <textarea
                  id="accruals-estimates-page__bulk-reason-input"
                  v-model="bulkReasonText"
                  rows="3"
                  class="accruals-estimates-page__bulk-reason-input"
                  :placeholder="bulkReasonMode === 'exclude' ? 'Why are you excluding this line?' : 'Tell the preparer what to fix (min 3 characters)'"
                  data-testid="accruals-estimates-page__bulk-reason-input"
                />
                <p
                  v-if="bulkReasonError"
                  class="accruals-estimates-page__bulk-reason-error"
                  role="alert"
                  data-testid="accruals-estimates-page__bulk-reason-error"
                >
                  {{ bulkReasonError }}
                </p>
              </div>
            </template>
            <template #actions>
              <div class="accruals-estimates-page__bulk-reason-actions">
                <base-button
                  small
                  secondary
                  class="shadow-off"
                  data-testid="accruals-estimates-page__bulk-reason-cancel-btn"
                  @click="closeBulkReasonDialog"
                >
                  Cancel
                </base-button>
                <base-button
                  small
                  primary
                  class="shadow-off"
                  :loading="bulkActionLoading"
                  data-testid="accruals-estimates-page__bulk-reason-confirm-btn"
                  @click="onConfirmBulkReject"
                >
                  Confirm
                </base-button>
              </div>
            </template>
          </lb-dialog>
        </template>

        <template v-else-if="activeTab === 'schedule'">
          <div class="accruals-estimates-page__toolbar">
            <div class="accruals-estimates-page__filters accruals-estimates-page__schedule-sort" data-testid="accruals-estimates-page__schedule-sort">
              <span class="accruals-estimates-page__sort-label">Sort :</span>
              <base-button
                v-for="sort in scheduleSortOptions"
                :key="sort.id"
                text
                plain
                depressed
                class="shadow-off accruals-estimates-page__filter-chip"
                :class="{ 'accruals-estimates-page__filter-chip--active': scheduleSort === sort.id }"
                :data-testid="`accruals-estimates-page__schedule-sort--${sort.id}`"
                @click="scheduleSort = sort.id"
              >
                <span class="accruals-estimates-page__filter-label">{{ sort.label }}</span>
              </base-button>
            </div>

            <div class="accruals-estimates-page__toolbar-actions">
              <div
                class="accruals-estimates-page__schedule-count"
                data-testid="accruals-estimates-page__schedule-count"
              >
                <span>Count :</span>
                <span data-testid="accruals-estimates-page__schedule-count-value">{{ filteredScheduleRows.length }}</span>
              </div>
              <base-button
                secondary
                class="shadow-off accruals-estimates-page__download-btn"
                :image="assets.SCHEDULE_DOWNLOAD"
                :image-width="16"
                image-alt=""
                :disabled="!filteredScheduleRows.length"
                data-testid="accruals-estimates-page__download-schedule-btn"
                @click="onDownloadSchedule"
              >
                Download JE
              </base-button>
              <accruals-estimates-overflow-menu
                :items="scheduleMenuItems"
                aria-label="More schedule actions"
                activator-testid="accruals-estimates-page__schedule-overflow-btn"
                menu-testid="accruals-estimates-page__schedule-overflow-menu"
                @select="onScheduleMenuSelect"
              />
            </div>
          </div>

          <div class="accruals-estimates-page__table-area flex-fill-table">
            <div class="accruals-estimates-page__content">
              <accruals-estimates-loader v-if="scheduleTableLoading" />
              <accruals-rules-empty-state
                v-else-if="!scheduleRows.length"
                :message="scheduleEmptyMessage"
                :show-actions="false"
                test-id="accruals-estimates-page__schedule-empty"
              />
              <accruals-schedule-table
                v-else
                ref="scheduleTable"
                :rows="pagedScheduleRows"
                :filter-options-data="scheduleRows"
                :expanded-ids="expandedScheduleIds"
                :items-per-page="itemsPerPage"
                @toggle-row="onToggleScheduleRow"
                @open-split-view="onOpenScheduleSplitView"
                @filter-item="onScheduleColumnFilter"
                @clear-filter="onClearScheduleColumnFilterState"
              />
              <accruals-estimate-detail-panel
                :visible="scheduleDetailOpen"
                :row="selectedScheduleRow"
                :can-review="false"
                :can-unlock="false"
                :project-id="projectId"
                @close="closeScheduleDetail"
                @exclude-from-cycle="onExcludeFromCycle"
                @view-info-request="onViewInfoRequest"
              />
            </div>
          </div>

        </template>
      </v-card-text>
    </v-card>

    <pagination
      v-if="activeTab === 'estimates'"
      :key="`${activeFilter}:${columnFilterKey}`"
      :list="filteredVendorGroups"
      :itemsperpage="itemsPerPage"
      data-testid="accruals-estimates-page__pagination"
      @paginated-data="setPaginatedData"
    />
    <pagination
      v-else-if="activeTab === 'schedule' && scheduleRows.length"
      :key="`schedule-${scheduleSort}:${scheduleColumnFilterKey}`"
      :list="filteredScheduleRows"
      :itemsperpage="itemsPerPage"
      data-testid="accruals-estimates-page__schedule-pagination"
      @paginated-data="setSchedulePaginatedData"
    />

    <accruals-re-run-rules-dialog
      v-if="rerunRulesDialogOpen"
      v-model="rerunRulesDialogOpen"
      :loading="rerunRulesLoading || snapshotRerunLoading"
      :extra-intro="rerunDialogExtraIntro"
      :affected-count="rerunDialogAffectedCount"
      @confirm="onConfirmRerunDialog"
    />
  </div>
</template>

<script>
import AccrualsEstimatesTable from './components/AccrualsEstimatesTable.vue';
import AccrualsScheduleTable from './components/AccrualsScheduleTable.vue';
import AccrualsEstimateDetailPanel from './components/AccrualsEstimateDetailPanel.vue';
import AccrualsEstimatesOverflowMenu from './components/AccrualsEstimatesOverflowMenu.vue';
import AccrualsReRunRulesDialog from './components/AccrualsReRunRulesDialog.vue';
import AccrualsEstimatesViewSwitcher from './components/AccrualsEstimatesViewSwitcher.vue';
import AccrualsRulesEmptyState from './components/AccrualsRulesEmptyState.vue';
import AccrualsEstimatesLoader from './components/AccrualsEstimatesLoader.vue';
import AccrualsEstimatesBulkBar from './components/AccrualsEstimatesBulkBar.vue';
import AccrualsEstimatesConfirmDialog from './components/AccrualsEstimatesConfirmDialog.vue';
import Pagination from '../../../components/Pagination/Pagination.vue';
import { PipelineStatusChips } from '../../../components';
import { ACCRUALS_ASSETS, ACCRUALS_ROUTES } from './accrualsPageConstants';
import {
  ACCRUALS_ESTIMATES_TABS,
  ACCRUALS_ESTIMATES_STATUS_CHIPS,
  ACCRUALS_ESTIMATE_STATUS_VARIANT,
  ACCRUALS_ESTIMATES_FILTERS,
  ACCRUALS_ESTIMATES_HEADER_MENU_ITEMS,
  ACCRUALS_ESTIMATES_TOOLBAR_MENU_ITEMS,
  ACCRUALS_SCHEDULE_SORT,
  ACCRUALS_SCHEDULE_EMPTY_MESSAGE,
  ACCRUALS_SCHEDULE_MENU_ITEMS,
  ACCRUALS_ESTIMATE_STATUS,
  ACCRUALS_ESTIMATE_ERRORS,
  ACCRUALS_WORKBENCH_ROLES,
  ACCRUALS_APPROVE_DIALOG,
  buildEstimatesBulkActionItems,
  ACCRUALS_ESTIMATE_AMOUNT_OPTION,
  buildEstimatesFilterCounts,
  buildEstimatesFilters,
  buildLocationMasterMap,
  applyLocationMasterToRows,
  defaultEstimateSource,
  defaultExpandLineSource,
  estimateAmountSourceLabel,
  estimateRefLabel,
  expandLineActiveAmount,
  formatEstimateMoney,
  groupEstimatesByVendor,
  isVendorLevelEstimateRow,
  mapEstimateApiToRow,
  findEstimateLineInRows,
  resolveEstimateDisplayLocation,
  parseEstimateMoney,
  validateCustomAmount,
  validateGlPair,
  validateRejectReason,
  vendorReferenceLabel,
  workbenchColumnStatusKey,
} from './accrualsEstimatesConstants';
import { isInfoRequestRuleConfigured } from './accrualsConfigurationStorage';
import {
  downloadScheduleCsv,
  mapScheduleApiToRow,
  mapScheduleRowToDetail,
  sortScheduleRows,
} from './components/AccrualsScheduleTableUtils';
import {
  formatComputationRunMeta,
  formatImSelectionRunMeta,
  isAccrualsComputationInProgress,
  isAccrualsImSelectionInProgress,
  pollAccrualsRun,
} from './accrualsRunPoll';

const COLUMN_FILTER_NONE = 'NONE_SELECTED';
const COLUMN_FILTER_FIELDS = ['vendor', 'location'];
const SCHEDULE_COLUMN_FILTER_FIELDS = ['location', 'vendor', 'glCode'];

function matchesEstimatesColumnFilter(value, selected) {
  if (!Array.isArray(selected) || !selected.length) return true;
  if (selected.includes(COLUMN_FILTER_NONE)) return false;
  const display = value == null || String(value).trim() === '' ? '—' : String(value);
  return selected.some((item) => {
    if (item == null || item === '') return display === '—';
    return String(item) === display;
  });
}

export default {
  name: 'AccrualsEstimates',
  components: {
    AccrualsEstimatesTable,
    AccrualsScheduleTable,
    AccrualsEstimateDetailPanel,
    AccrualsEstimatesOverflowMenu,
    AccrualsReRunRulesDialog,
    AccrualsEstimatesViewSwitcher,
    AccrualsRulesEmptyState,
    AccrualsEstimatesLoader,
    AccrualsEstimatesBulkBar,
    AccrualsEstimatesConfirmDialog,
    Pagination,
    PipelineStatusChips,
  },
  data() {
    return {
      assets: ACCRUALS_ASSETS,
      tabs: ACCRUALS_ESTIMATES_TABS,
      filters: ACCRUALS_ESTIMATES_FILTERS.map((f) => ({ ...f })),
      estimatesStatusChips: ACCRUALS_ESTIMATES_STATUS_CHIPS,
      estimatesStatusVariantMap: ACCRUALS_ESTIMATE_STATUS_VARIANT,
      allRows: [],
      initialLoading: true,
      emptyNotice: '',
      activeTab: 'estimates',
      activeFilter: 'all',
      locationFilters: {},
      hasActiveTableFilter: false,
      selectedIds: [],
      selectedRow: null,
      detailPanelOpen: false,
      statusUpdating: false,
      detailRefreshKey: 0,
      paginationPage: 1,
      itemsPerPage: 25,
      expandedGroupIds: [],
      sourceByGroupId: {},
      confirmingGroupId: null,
      savingGroupId: null,
      rerunLoading: false,
      rerunRulesLoading: false,
      rerunRulesDialogOpen: false,
      rerunDialogMode: 'rules',
      snapshotRerunLoading: false,
      refreshLoading: false,
      bulkActionLoading: false,
      bulkReasonOpen: false,
      bulkReasonMode: 'reject',
      bulkReasonRows: [],
      bulkReasonText: '',
      bulkReasonError: '',
      bulkApproveOpen: false,
      bulkApproveRows: [],
      bulkApproveNote: '',
      scheduleRows: [],
      scheduleSort: 'location',
      scheduleColumnFilters: {},
      expandedScheduleIds: [],
      schedulePaginationPage: 1,
      prepareScheduleLoading: false,
      scheduleDetailOpen: false,
      selectedScheduleRow: null,
      runPollGeneration: 0,
      imSelectionPolling: false,
      computationRunPolling: false,
    };
  },
  computed: {
    projectId() {
      return this.$route.params.id;
    },
    access() {
      return this.$store.state.m_financialclose?.accruals?.access || {};
    },
    workbenchRole() {
      const access = this.access || {};
      const primary = String(access.persona || '').toLowerCase();
      const personas = Array.isArray(access.personas) ? access.personas : [];
      const names = personas.map((persona) => String(persona).toLowerCase());
      if (access.canReviewEstimates || primary === ACCRUALS_WORKBENCH_ROLES.PREPARER
        || names.includes(ACCRUALS_WORKBENCH_ROLES.PREPARER)) {
        return ACCRUALS_WORKBENCH_ROLES.PREPARER;
      }
      if (primary === ACCRUALS_WORKBENCH_ROLES.APPROVER
        || names.includes(ACCRUALS_WORKBENCH_ROLES.APPROVER)) {
        return ACCRUALS_WORKBENCH_ROLES.APPROVER;
      }
      if (primary === ACCRUALS_WORKBENCH_ROLES.MANAGER
        || names.includes(ACCRUALS_WORKBENCH_ROLES.MANAGER)) {
        return ACCRUALS_WORKBENCH_ROLES.MANAGER;
      }
      return ACCRUALS_WORKBENCH_ROLES.PREPARER;
    },
    isApproverRole() {
      return this.workbenchRole === ACCRUALS_WORKBENCH_ROLES.APPROVER;
    },
    isReviewerRole() {
      return this.isApproverRole || this.workbenchRole === ACCRUALS_WORKBENCH_ROLES.MANAGER;
    },
    canApproveEstimates() {
      return (this.approveLocationKeys || []).length > 0;
    },
    approveLocationKeys() {
      return this.access.approveLocationKeys || [];
    },
    canRunEstimation() {
      return Boolean(this.access.canRunEstimation);
    },
    canReviewEstimates() {
      return Boolean(this.access.canReviewEstimates);
    },
    reviewLocationKeys() {
      return this.access.reviewLocationKeys || [];
    },
    canReviewSelectedRow() {
      return this.rowAllowsReview(this.selectedRow);
    },
    detailRole() {
      if (this.canReviewSelectedRow) return ACCRUALS_WORKBENCH_ROLES.PREPARER;
      if (this.canApproveSelectedRow) return ACCRUALS_WORKBENCH_ROLES.APPROVER;
      const personas = Array.isArray(this.access.personas) ? this.access.personas : [];
      const names = personas.map((persona) => String(persona).toLowerCase());
      if (names.includes(ACCRUALS_WORKBENCH_ROLES.MANAGER) || this.canUnlockEstimates) {
        return ACCRUALS_WORKBENCH_ROLES.MANAGER;
      }
      if (names.includes(ACCRUALS_WORKBENCH_ROLES.APPROVER)) {
        return ACCRUALS_WORKBENCH_ROLES.APPROVER;
      }
      return this.workbenchRole;
    },
    canApproveSelectedRow() {
      return this.rowAllowsApprove(this.selectedRow);
    },
    canUnlockEstimates() {
      return Boolean(this.access.canUnlockEstimates);
    },
    rerunFreshness() {
      return this.$store.state.m_financialclose?.accruals?.rerunFreshness || {};
    },
    snapshotStale() {
      return Boolean(this.rerunFreshness.stale);
    },
    rerunDialogExtraIntro() {
      if (this.rerunDialogMode !== 'snapshot') return '';
      return 'Both the Inference estimate and the Computation Rule estimate will be reverted and recalculated for the affected population.';
    },
    rerunDialogAffectedCount() {
      if (this.rerunDialogMode !== 'snapshot') return null;
      return this.rerunFreshness.affected_count;
    },
    headerMenuItems() {
      return ACCRUALS_ESTIMATES_HEADER_MENU_ITEMS;
    },
    toolbarMenuItems() {
      let items = ACCRUALS_ESTIMATES_TOOLBAR_MENU_ITEMS;
      if (!this.canReviewEstimates) {
        items = items.filter((item) => item.id !== 'prepare-schedule');
      }
      if (!this.canRunEstimation) {
        items = items.filter((item) => (
          item.id !== 'rerun-noa' && item.id !== 'rerun-rules'
        ));
      }
      return items;
    },
    scheduleSortOptions() {
      return ACCRUALS_SCHEDULE_SORT;
    },
    scheduleEmptyMessage() {
      return ACCRUALS_SCHEDULE_EMPTY_MESSAGE;
    },
    scheduleMenuItems() {
      return ACCRUALS_SCHEDULE_MENU_ITEMS;
    },
    estimatesLoading() {
      return Boolean(this.$store.state.m_financialclose?.accruals?.estimatesLoading);
    },
    tableLoading() {
      return this.initialLoading || (this.estimatesLoading && !this.allRows.length);
    },
    scheduleTableLoading() {
      return this.scheduleLoading && !this.scheduleRows.length;
    },
    scheduleLoading() {
      return Boolean(this.$store.state.m_financialclose?.accruals?.scheduleLoading);
    },
    imSelectionLoading() {
      return Boolean(this.$store.state.m_financialclose?.accruals?.imSelectionLoading);
    },
    headerRefreshLoading() {
      return this.refreshLoading || (
        (this.activeTab === 'schedule' ? this.scheduleLoading : this.estimatesLoading)
        && !this.rerunLoading
        && !this.rerunRulesLoading
        && !this.imSelectionLoading
        && !this.imSelectionPolling
        && !this.computationRunPolling
        && !this.prepareScheduleLoading
      );
    },
    pageLoading() {
      return (
        this.estimatesLoading
        || this.scheduleLoading
        || this.rerunLoading
        || this.rerunRulesLoading
        || this.imSelectionLoading
        || this.imSelectionPolling
        || this.computationRunPolling
        || this.prepareScheduleLoading
      );
    },
    estimatesStatusSummary() {
      return (this.filters || []).reduce((summary, filter) => {
        summary[filter.id] = filter.count;
        return summary;
      }, {});
    },
    vendorGroups() {
      return groupEstimatesByVendor(this.allRows, this.activeFilter);
    },
    columnFilterKey() {
      const filters = this.locationFilters || {};
      return COLUMN_FILTER_FIELDS
        .map((field) => {
          const selected = filters[field];
          return Array.isArray(selected) ? selected.join('|') : '';
        })
        .join('::');
    },
    filteredVendorGroups() {
      const groups = this.vendorGroups || [];
      const filters = this.locationFilters || {};
      return groups.filter((group) => (
        matchesEstimatesColumnFilter(group.vendor, filters.vendor)
        && matchesEstimatesColumnFilter(group.location, filters.location)
      ));
    },
    tableRows() {
      const source = this.filteredVendorGroups || [];
      const perPage = this.itemsPerPage > 0 ? this.itemsPerPage : 25;
      const start = (this.paginationPage - 1) * perPage;
      return source.slice(start, start + perPage);
    },
    sortedScheduleRows() {
      return sortScheduleRows(this.scheduleRows, this.scheduleSort);
    },
    scheduleColumnFilterKey() {
      const filters = this.scheduleColumnFilters || {};
      return SCHEDULE_COLUMN_FILTER_FIELDS
        .map((field) => {
          const selected = filters[field];
          return Array.isArray(selected) ? selected.join('|') : '';
        })
        .join('::');
    },
    filteredScheduleRows() {
      const filters = this.scheduleColumnFilters || {};
      return (this.sortedScheduleRows || []).filter((row) => (
        SCHEDULE_COLUMN_FILTER_FIELDS.every((field) => (
          matchesEstimatesColumnFilter(row[field], filters[field])
        ))
      ));
    },
    pagedScheduleRows() {
      const source = this.filteredScheduleRows || [];
      const perPage = this.itemsPerPage > 0 ? this.itemsPerPage : 25;
      const start = (this.schedulePaginationPage - 1) * perPage;
      return source.slice(start, start + perPage);
    },
    bulkSelectionActions() {
      return buildEstimatesBulkActionItems(this.selectedIds.length, this.workbenchRole).map((action) => ({
        id: action.id,
        label: action.label,
        iconSrc: this.assets[action.iconKey],
        iconInvert: action.iconInvert,
        testId: `accruals-estimates-bulk-action-bar__action--${action.id}`,
        labelTestId: `accruals-estimates-bulk-action-bar__action-label--${action.id}`,
      }));
    },
    filterEmptyMessage() {
      if (this.vendorGroups.length || !this.allRows.length) return '';
      switch (this.activeFilter) {
        case ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION:
          return this.isReviewerRole
            ? 'No estimates waiting with the preparer.'
            : 'Nothing waiting for submission.';
        case ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL:
          return this.isReviewerRole
            ? 'No estimates waiting for your approval.'
            : 'Nothing waiting for approval.';
        case ACCRUALS_ESTIMATE_STATUS.APPROVED:
          return 'No approved estimates in this view yet.';
        case ACCRUALS_ESTIMATE_STATUS.REJECTED:
          return 'No rejected estimates in this view.';
        case ACCRUALS_ESTIMATE_STATUS.EXCLUDED:
          return 'No excluded estimates in this view.';
        default:
          return 'No estimates in this view.';
      }
    },
    filterEmptyHint() {
      if (!this.filterEmptyMessage || this.activeFilter === 'all') return '';
      return 'Switch to All to see every estimate.';
    },
    bulkReasonRefs() {
      return (this.bulkReasonRows || []).map(estimateRefLabel).join(', ');
    },
    bulkApproveCopy() {
      return ACCRUALS_APPROVE_DIALOG;
    },
    bulkApproveHeading() {
      return `Approve selected estimates (${this.bulkApproveItems.length})`;
    },
    selectedEstimateLineCount() {
      return (this.vendorGroups || []).reduce((sum, group) => {
        if (!this.selectedIds.includes(group._id)) return sum;
        const lines = (group.children || []).filter((row) => (
          row && !isVendorLevelEstimateRow(row)
        ));
        return sum + lines.length;
      }, 0);
    },
    bulkApproveItems() {
      return (this.bulkApproveRows || []).map((entry, index) => {
        const row = entry.row || entry;
        const group = entry.group || {};
        const label = vendorReferenceLabel(group, row);
        const source = row.amountSource || row.raw?.preparer_amount_source;
        const amount = row.preparerAmount
          ?? row.raw?.preparer_amount
          ?? row.postAmount
          ?? row.raw?.final_accrual_amount;
        const formatted = formatEstimateMoney(amount);
        return {
          key: String(row._id || row.lineKey || index),
          label,
          vendor: group.vendor || row.vendor || '—',
          sourceLabel: estimateAmountSourceLabel(source),
          amount: formatted === '—' ? formatted : `$${formatted}`,
          muted: /^(V|G)-/i.test(String(label || '')),
        };
      });
    },
  },
  watch: {
    activeFilter() {
      this.selectedIds = [];
      this.closeDetailPanel();
      this.paginationPage = 1;
      this.expandedGroupIds = [];
      this.sourceByGroupId = {};
    },
    workbenchRole(next, prev) {
      if (next === prev) return;
      console.log('[AccrualsEstimates]:', { action: 'role-change', role: next });
    },
    scheduleSort() {
      this.schedulePaginationPage = 1;
      this.expandedScheduleIds = [];
    },
    bulkApproveOpen(open) {
      if (open) return;
      this.bulkApproveRows = [];
      this.bulkApproveNote = '';
    },
    projectId: {
      immediate: true,
      handler(id) {
        if (!id) return;
        this.$store.dispatch('accruals/loadAccess', { projectId: id });
        this.bootstrapEstimates();
      },
    },
  },
  beforeDestroy() {
    this.runPollGeneration += 1;
    this.imSelectionPolling = false;
    this.computationRunPolling = false;
    this.closeDetailPanel();
    this.expandedGroupIds = [];
    this.sourceByGroupId = {};
    this.confirmingGroupId = null;
    this.savingGroupId = null;
    this.selectedIds = [];
    this.initialLoading = true;
    this.bulkReasonOpen = false;
    this.bulkReasonRows = [];
    this.bulkReasonText = '';
    this.bulkReasonError = '';
    this.closeBulkApproveDialog();
    this.expandedScheduleIds = [];
    this.scheduleRows = [];
    this.locationFilters = {};
    this.hasActiveTableFilter = false;
    this.closeScheduleDetail();
  },
  methods: {
    locationCandidates(row) {
      const raw = row?.raw || {};
      return [
        row?.locationKey,
        row?.subsidiary_code,
        row?.location,
        raw.location_key,
        raw.subsidiary_code,
        raw.subsidiary,
        raw.location,
      ]
        .filter((value) => value != null && String(value).trim() !== '')
        .map((value) => String(value).trim().toLowerCase());
    },
    locationAllowed(row, keys) {
      if (!row || !(keys || []).length) return false;
      const allowed = new Set((keys || []).map((key) => String(key).trim().toLowerCase()));
      return this.locationCandidates(row).some((value) => allowed.has(value));
    },
    rowAllowsReview(row) {
      if (!this.canReviewEstimates || !row) return false;
      return this.locationAllowed(row, this.reviewLocationKeys);
    },
    rowAllowsApprove(row) {
      if (!row || this.rowAllowsReview(row)) return false;
      const personas = Array.isArray(this.access.personas) ? this.access.personas : [];
      const names = personas.map((persona) => String(persona).toLowerCase());
      const canApprove = this.isReviewerRole
        || names.includes(ACCRUALS_WORKBENCH_ROLES.APPROVER)
        || names.includes(ACCRUALS_WORKBENCH_ROLES.MANAGER);
      if (!canApprove) return false;
      return this.locationAllowed(row, this.approveLocationKeys);
    },
    async bootstrapEstimates() {
      await this.fetchEstimates();
      await this.resumeAccrualsRunPolls();
    },
    async fetchEstimates() {
      if (!this.projectId) return;
      try {
        const data = await this.$store.dispatch('accruals/loadEstimates', {
          projectId: this.projectId,
          cycleId: this.projectId,
          page: 1,
          limit: 500,
          filter: 'all',
        });
        const rows = (data?.rows || []).map(mapEstimateApiToRow).filter(Boolean);
        const master = await this.$store.dispatch('accruals/loadLocationMaster').catch(() => []);
        this.allRows = applyLocationMasterToRows(rows, buildLocationMasterMap(master));
        this.emptyNotice = rows.length ? '' : (data?.access_message || 'No data found');
        const groups = groupEstimatesByVendor(rows, 'all');
        this.filters = buildEstimatesFilters(buildEstimatesFilterCounts(groups));
        await this.$store.dispatch('accruals/loadRerunFreshness', { projectId: this.projectId });
        console.log('[AccrualsEstimates]:', { action: 'loaded', count: rows.length, total: data?.total });
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.LOAD_FAILED);
        this.allRows = [];
        this.emptyNotice = 'No data found';
      } finally {
        this.initialLoading = false;
      }
    },
    async resumeAccrualsRunPolls() {
      if (!this.projectId) return;
      try {
        const [im, computation] = await Promise.all([
          this.$store.dispatch('accruals/loadImSelectionStatus', { projectId: this.projectId }),
          this.$store.dispatch('accruals/loadComputationRunStatus', { projectId: this.projectId }),
        ]);
        const waits = [];
        if (isAccrualsImSelectionInProgress(im?.status)) {
          waits.push(this.pollImSelectionUntilSettled(im.selection_run_id));
        }
        if (isAccrualsComputationInProgress(computation?.status)) {
          waits.push(this.pollComputationUntilSettled(computation.computation_run_id));
        }
        if (!waits.length) return;
        await Promise.all(waits);
        await this.fetchEstimates();
      } catch (error) {
        this.reportWorkbenchError(error, 'Could not resume the estimation run. Try again.');
      }
    },
    async pollAccrualsJobsUntilSettled({ im = false, computation = false } = {}) {
      const waits = [];
      if (im) waits.push(this.pollImSelectionUntilSettled());
      if (computation) waits.push(this.pollComputationUntilSettled());
      if (waits.length) await Promise.all(waits);
    },
    async pollImSelectionUntilSettled(selectionRunId) {
      const generation = this.runPollGeneration;
      this.imSelectionPolling = true;
      try {
        const last = await pollAccrualsRun({
          fetchStatus: () => this.$store.dispatch('accruals/loadImSelectionStatus', {
            projectId: this.projectId,
            selectionRunId,
          }),
          isInProgress: isAccrualsImSelectionInProgress,
          shouldStop: () => generation !== this.runPollGeneration,
        });
        if (generation !== this.runPollGeneration) return last;
        this.notifyImSelectionSettled(last);
        return last;
      } catch (error) {
        this.reportWorkbenchError(error, 'IM selection status check failed. Try again.');
        return null;
      } finally {
        if (generation === this.runPollGeneration) this.imSelectionPolling = false;
      }
    },
    async pollComputationUntilSettled(runId) {
      const generation = this.runPollGeneration;
      this.computationRunPolling = true;
      try {
        const last = await pollAccrualsRun({
          fetchStatus: () => this.$store.dispatch('accruals/loadComputationRunStatus', {
            projectId: this.projectId,
            runId,
          }),
          isInProgress: isAccrualsComputationInProgress,
          shouldStop: () => generation !== this.runPollGeneration,
        });
        if (generation !== this.runPollGeneration) return last;
        this.notifyComputationSettled(last);
        return last;
      } catch (error) {
        this.reportWorkbenchError(error, 'Estimation run status check failed. Try again.');
        return null;
      } finally {
        if (generation === this.runPollGeneration) this.computationRunPolling = false;
      }
    },
    notifyImSelectionSettled(run) {
      if (isAccrualsImSelectionInProgress(run?.status) || !run?.status) return;
      if (run.status === 'failed') {
        this.$store.commit('sbError', run.error || 'IM selection failed', { root: true });
        return;
      }
      if (run.status === 'completed') {
        this.$store.commit(
          'sbSuccess',
          formatImSelectionRunMeta(run) || 'IM selection completed',
          { root: true },
        );
      }
    },
    notifyComputationSettled(run) {
      if (isAccrualsComputationInProgress(run?.status) || !run?.status) return;
      if (run.status === 'failed') {
        this.$store.commit('sbError', run.error || 'Estimation run failed', { root: true });
        return;
      }
      if (run.status === 'completed') {
        this.$store.commit(
          'sbSuccess',
          formatComputationRunMeta(run) || 'Estimation run completed',
          { root: true },
        );
      }
    },
    applyScheduleData(data) {
      this.scheduleRows = (data?.rows || []).map(mapScheduleApiToRow).filter(Boolean);
      this.scheduleColumnFilters = {};
      this.expandedScheduleIds = [];
      this.schedulePaginationPage = 1;
    },
    async fetchSchedule() {
      if (!this.projectId) return;
      try {
        const data = await this.$store.dispatch('accruals/loadSchedule', {
          projectId: this.projectId,
        });
        this.applyScheduleData(data);
        console.log('[AccrualsEstimates]:', {
          action: 'schedule-loaded',
          count: this.scheduleRows.length,
        });
      } catch (error) {
        this.reportWorkbenchError(error, 'Could not load the schedule. Try again.');
        this.scheduleRows = [];
      }
    },
    onOpenSplitView(row) {
      if (!row) return;
      if (row.isGroup) {
        const target = row.vendorRuleRow || (row.children || [])[0] || row;
        const groupLocation = String(row.location || '').trim();
        this.selectedRow = groupLocation && groupLocation !== '—'
          ? { ...target, location: row.location }
          : target;
      } else {
        this.selectedRow = row;
      }
      this.detailPanelOpen = true;
      console.log('[AccrualsEstimates]:', { action: 'open-split-view', rowId: this.selectedRow?._id });
    },
    onSourceChange({ group, source }) {
      if (!group?._id || !source) return;
      this.$set(this.sourceByGroupId, group._id, source);
      if (source === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM && !this.expandedGroupIds.includes(group._id)) {
        this.expandedGroupIds = [...this.expandedGroupIds, group._id];
      }
      console.log('[AccrualsEstimates]:', { action: 'source-change', groupId: group._id, source });
    },
    onRowMenu({ group, row, item }) {
      const target = row || group?.vendorRuleRow || (group?.children || [])[0] || group || null;
      this.selectedRow = target;
      console.log('[AccrualsEstimates]:', { action: 'row-menu', item: item?.id, rowId: target?._id });
      if (!item || !target) return;
      if (item.id === 'view-details') {
        this.onOpenSplitView(target);
        return;
      }
      if (item.id === 'view-info-request') {
        this.onViewInfoRequest();
        return;
      }
      if (item.id === 'submit-for-approval') {
        this.guardPreparer(() => this.submitEstimateLines([target], group));
        return;
      }
      if (item.id === 'approve') {
        this.guardApprover(() => this.approveEstimateLines([target]));
        return;
      }
      if (item.id === 'reject' || item.id === 'request-resubmission') {
        this.detailPanelOpen = true;
        if (item.id === 'request-resubmission') {
          this.$store.commit('sbInfo', 'Add a reason in the panel, then confirm the resubmission request.', { root: true });
        } else {
          this.$store.commit('sbInfo', 'Add a rejection reason in the panel, then confirm.', { root: true });
        }
        return;
      }
      if (item.id === 'exclude-from-cycle') {
        this.onExcludeFromCycle({ row: target });
        return;
      }
      if (item.id === 'include-in-cycle') {
        this.onIncludeInCycle({ row: target });
      }
    },
    guardPreparer(next) {
      if (!this.canReviewEstimates) {
        this.$store.commit('sbError', 'Only the cycle Preparer can submit estimates.', { root: true });
        return Promise.resolve(null);
      }
      return Promise.resolve()
        .then(() => next())
        .catch((error) => this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.SUBMIT_FAILED));
    },
    guardApprover(next) {
      if (!this.canApproveEstimates) {
        this.$store.commit('sbError', 'Only the cycle Approver can decide on submitted estimates.', { root: true });
        return Promise.resolve(null);
      }
      return Promise.resolve()
        .then(() => next())
        .catch((error) => this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.APPROVE_FAILED));
    },
    async onConfirmGroup(payload) {
      const group = payload?.group;
      const rows = Array.isArray(payload?.items) && payload.items.length
        ? payload.items.map((item) => item.row).filter(Boolean)
        : [payload?.row || group?.vendorRuleRow || (group?.children || [])[0] || null].filter(Boolean);
      if (!rows.length) return;
      if (!this.canReviewEstimates) {
        this.$store.commit('sbError', 'Only the cycle Preparer can submit estimates.', { root: true });
        return;
      }
      const key = rows[0]._id || group?._id;
      this.confirmingGroupId = key || null;
      try {
        if (Array.isArray(payload?.items) && payload.items.some((item) => item?.source)) {
          await this.submitStructuredItems(payload.items);
        } else {
          await this.submitEstimateLines(rows, group);
        }
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.SUBMIT_FAILED);
      } finally {
        this.confirmingGroupId = null;
      }
    },
    async onSubmitEstimate(payload) {
      console.log('[AccrualsEstimates]:', { action: 'submit-estimate', rowId: payload?.row?._id });
      if (!this.canReviewEstimates) {
        this.$store.commit('sbError', 'Only the cycle Preparer can submit estimates.', { root: true });
        return;
      }
      try {
        await this.submitEstimateLines(payload?.row, null, {
          note: payload?.note || '',
          option: payload?.option || '',
          customAmount: payload?.customAmount ?? null,
          debitGl: payload?.debitGl || '',
          creditGl: payload?.creditGl || '',
        });
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.SUBMIT_FAILED);
      }
    },
    async onRejectEstimate(payload) {
      const rows = this.decisionRows(payload).filter((row) => (
        this.lineTargetStatus(row) === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
      ));
      console.log('[AccrualsEstimates]:', { action: 'reject-estimate', count: rows.length });
      if (!this.canApproveEstimates) {
        this.$store.commit('sbError', 'Only the cycle Approver can reject estimates.', { root: true });
        return;
      }
      const key = rows[0]?._id || payload?.group?._id || null;
      this.confirmingGroupId = key;
      try {
        await this.rejectEstimateLines(rows, payload?.reason);
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.REJECT_FAILED);
      } finally {
        this.confirmingGroupId = null;
      }
    },
    async onRequestResubmission({ row, reason }) {
      console.log('[AccrualsEstimates]:', { action: 'request-resubmission', rowId: row?._id });
      if (!this.canApproveEstimates && !this.canUnlockEstimates) {
        this.$store.commit('sbError', 'Only the cycle Approver or Manager can request resubmission.', { root: true });
        return;
      }
      try {
        await this.requestResubmissionLines([row || this.selectedRow].filter(Boolean), reason);
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.RESUBMIT_FAILED);
      }
    },
    async onSaveGl(payload) {
      const group = payload?.group;
      const entries = Array.isArray(payload?.items) && payload.items.length
        ? payload.items
        : [{
          row: payload?.row || group?.vendorRuleRow || (group?.children || [])[0] || null,
          debitGl: payload.debitGl,
          creditGl: payload.creditGl,
        }];
      const reviewRow = entries[0]?.row;
      if (!reviewRow || !this.canReviewEstimates) {
        if (!this.canReviewEstimates) {
          this.$store.commit('sbError', 'Only the cycle Preparer can assign GL accounts.', { root: true });
        }
        return;
      }
      const invalidEntry = entries.find((entry) => {
        const errors = validateGlPair(entry.debitGl, entry.creditGl);
        return errors.debit || errors.credit || errors.note;
      });
      if (invalidEntry) {
        const errors = validateGlPair(invalidEntry.debitGl, invalidEntry.creditGl);
        const message = errors.note || errors.debit || errors.credit;
        this.$store.commit('sbError', message, { root: true });
        console.log('[AccrualsEstimates]:', {
          action: 'save-gl-validation-failed',
          rowId: invalidEntry.row?._id,
          errors,
        });
        return;
      }
      this.savingGroupId = reviewRow._id || group?._id;
      try {
        await this.$store.dispatch('accruals/assignEstimateGl', {
          projectId: this.projectId,
          items: entries.map((entry) => ({
            estimate_id: entry.row?.estimateId || entry.row?.raw?._id || entry.row?._id,
            line_key: entry.row?.lineKey || null,
            debit_gl: entry.debitGl,
            credit_gl: entry.creditGl,
          })),
        });
        await this.fetchEstimates();
        this.rebindSelectedRow();
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.RECORD_FAILED);
      } finally {
        this.savingGroupId = null;
      }
    },
    onFollowUp(target) {
      const row = target?.informationRequestId
        ? target
        : target?.vendorRuleRow
          || (target?.children || []).find((child) => child.informationRequestId)
          || target;
      this.onInfoRequestAction(row || target);
    },
    onToggleGroup(group) {
      const id = group?._id;
      if (!id) return;
      const next = new Set(this.expandedGroupIds || []);
      const collapsing = next.has(id);
      if (collapsing) next.delete(id);
      else next.add(id);
      this.expandedGroupIds = Array.from(next);
      if (collapsing && this.detailPanelOpen && this.selectedRowBelongsToGroup(group)) {
        this.closeDetailPanel();
      }
      console.log('[AccrualsEstimates]:', { action: 'toggle-vendor-group', groupId: id, expanded: next.has(id) });
    },
    selectedRowBelongsToGroup(group) {
      const selected = this.selectedRow;
      if (!group || !selected) return false;
      if (selected._id && selected._id === group._id) return true;
      return (group.children || []).some((child) => (
        (child?._id && child._id === selected._id)
        || (child?.lineKey && selected.lineKey && String(child.lineKey) === String(selected.lineKey))
      ));
    },
    closeDetailPanel() {
      this.detailPanelOpen = false;
      this.selectedRow = null;
    },
    beginStatusRefresh() {
      if (this.detailPanelOpen) this.statusUpdating = true;
    },
    applyTransitionItems(items) {
      (items || []).forEach((item) => {
        if (!item?.line) return;
        this.patchEstimateLine(item.line);
      });
    },
    patchEstimateLine(line) {
      const estimateId = String(line.estimate_id || line.estimateId || '');
      const lineKey = String(line.line_key || line.lineKey || '');
      const status = line.workbench_status || line.workbenchStatus;
      if (!estimateId) return;
      this.allRows = (this.allRows || []).map((vendor) => {
        const vendorId = String(vendor.estimateId || vendor._id || '');
        if (vendorId !== estimateId) return vendor;
        const lineRows = (vendor.lineRows || []).map((existing) => {
          if (lineKey && String(existing.lineKey || '') !== lineKey) return existing;
          return {
            ...existing,
            ...(status ? { status, workbenchStatus: status } : {}),
            locked: line.locked != null ? Boolean(line.locked) : existing.locked,
            raw: { ...(existing.raw || {}), ...line, ...(status ? { workbench_status: status, review_status: line.review_status || status } : {}) },
          };
        });
        return { ...vendor, lineRows };
      });
      if (this.selectedRow && String(this.selectedRow.estimateId || this.selectedRow._id || '') === estimateId) {
        const selectedKey = String(this.selectedRow.lineKey || '');
        if (!lineKey || selectedKey === lineKey) {
          this.selectedRow = {
            ...this.selectedRow,
            ...(status ? { status, workbenchStatus: status } : {}),
            raw: { ...(this.selectedRow.raw || {}), ...line, ...(status ? { workbench_status: status, review_status: line.review_status || status } : {}) },
          };
        }
      }
    },
    applyEstimateLine(line) {
      if (!line) return;
      const estimateId = String(line.estimateId || '');
      const lineKey = String(line.lineKey || '');
      this.allRows = (this.allRows || []).map((vendor) => {
        if (String(vendor.estimateId || vendor._id || '') !== estimateId) return vendor;
        const lineRows = (vendor.lineRows || []).map((existing) => (
          String(existing.lineKey || '') === lineKey
            ? {
              ...existing,
              ...line,
              location: existing.location,
              locationKey: existing.locationKey || line.locationKey,
              raw: { ...(existing.raw || {}), ...(line.raw || existing.raw || {}) },
            }
            : existing
        ));
        return { ...vendor, lineRows };
      });
      if (this.selectedRow && String(this.selectedRow.lineKey || '') === lineKey
        && String(this.selectedRow.estimateId || '') === estimateId) {
        const masterMap = buildLocationMasterMap(
          this.$store.state.m_financialclose?.accruals?.locationMaster || [],
        );
        const merged = { ...this.selectedRow, ...line };
        const display = resolveEstimateDisplayLocation(merged, masterMap)
          || resolveEstimateDisplayLocation(this.selectedRow, masterMap);
        this.selectedRow = display ? { ...merged, location: display } : merged;
      }
    },
    async finishStatusChange(data) {
      this.applyTransitionItems(data?.items);
      await this.fetchEstimates();
      this.rebindSelectedRow();
      if (this.detailPanelOpen && this.selectedRow) this.detailRefreshKey += 1;
      else this.statusUpdating = false;
      return data;
    },
    rebindSelectedRow() {
      if (!this.selectedRow) return;
      const next = findEstimateLineInRows(this.allRows, this.selectedRow);
      if (next) this.selectedRow = next;
      console.log('[AccrualsEstimates]:', {
        action: 'rebind-selected-row',
        rowId: this.selectedRow?._id,
        status: this.selectedRow?.workbenchStatus || this.selectedRow?.status,
      });
    },
    closeScheduleDetail() {
      this.scheduleDetailOpen = false;
      this.selectedScheduleRow = null;
    },
    onOpenScheduleSplitView(row) {
      this.selectedScheduleRow = mapScheduleRowToDetail(row);
      this.scheduleDetailOpen = Boolean(this.selectedScheduleRow);
      console.log('[AccrualsEstimates]:', { action: 'open-schedule-split-view', rowId: row?._id });
    },
    onTabChange(tabId) {
      this.activeTab = tabId;
      console.log('[AccrualsEstimates]:', { tab: tabId });
      if (tabId === 'schedule') {
        this.closeDetailPanel();
        this.fetchSchedule();
        return;
      }
      this.closeScheduleDetail();
    },
    setSchedulePaginatedData(paginationInfo) {
      const page = paginationInfo?.page || 1;
      if (page !== this.schedulePaginationPage) {
        this.expandedScheduleIds = [];
      }
      this.schedulePaginationPage = page;
    },
    onToggleScheduleRow(row) {
      const id = row?._id;
      if (!id) return;
      const next = new Set(this.expandedScheduleIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      this.expandedScheduleIds = Array.from(next);
      console.log('[AccrualsEstimates]:', { action: 'toggle-schedule-row', rowId: id, expanded: next.has(id) });
    },
    setPaginatedData(paginationInfo) {
      const page = paginationInfo?.page || 1;
      if (page !== this.paginationPage) {
        this.selectedIds = [];
        this.closeDetailPanel();
        this.expandedGroupIds = [];
      }
      this.paginationPage = page;
    },
    async onHeaderRefresh() {
      if (this.headerRefreshLoading || !this.projectId) return;
      console.log('[AccrualsEstimates]:', { action: 'refresh', projectId: this.projectId });
      this.refreshLoading = true;
      try {
        if (this.activeTab === 'schedule') {
          await this.fetchSchedule();
          this.$store.commit('sbSuccess', 'Schedule refreshed', { root: true });
        } else {
          await this.fetchEstimates();
          this.$store.commit('sbSuccess', 'Estimates refreshed', { root: true });
        }
      } catch (error) {
        this.reportWorkbenchError(error, 'Refresh failed. Try again.');
      } finally {
        this.refreshLoading = false;
      }
    },
    async onReRunEstimate() {
      console.log('[AccrualsEstimates]:', { action: 're-run-estimate', projectId: this.projectId });
      if (!this.canRunEstimation) return;
      await this.$store.dispatch('accruals/loadRerunFreshness', { projectId: this.projectId });
      if (this.snapshotStale) {
        this.rerunDialogMode = 'snapshot';
        this.rerunRulesDialogOpen = true;
        return;
      }
      this.rerunLoading = true;
      try {
        await this.$store.dispatch('accruals/runInferenceBatch', {
          projectId: this.projectId,
          cycleId: this.projectId,
        });
        await this.fetchEstimates();
      } catch (error) {
        this.reportWorkbenchError(error, 'Re-run failed. Try again.');
      } finally {
        this.rerunLoading = false;
      }
    },
    async onConfirmRerunDialog() {
      if (this.rerunDialogMode === 'snapshot') {
        await this.onConfirmSnapshotRerun();
        return;
      }
      await this.onReRunRulesConfirm();
    },
    async onConfirmSnapshotRerun() {
      if (this.snapshotRerunLoading || !this.canRunEstimation) return;
      this.snapshotRerunLoading = true;
      try {
        await this.$store.dispatch('accruals/startAccrualsRerun', {
          projectId: this.projectId,
          scenario: 'snapshot',
        });
        this.rerunRulesDialogOpen = false;
        await this.pollAccrualsJobsUntilSettled({ im: true, computation: true });
        await this.fetchEstimates();
      } catch (error) {
        this.reportWorkbenchError(error, 'Snapshot re-run failed. Try again.');
      } finally {
        this.snapshotRerunLoading = false;
      }
    },
    async onReRunRulesConfirm() {
      console.log('[AccrualsEstimates]:', { action: 're-run-rules', projectId: this.projectId });
      if (this.rerunRulesLoading || this.imSelectionLoading) return;

      this.rerunRulesLoading = true;
      try {
        let imRule = this.$store.state.m_financialclose?.accruals?.imRule;
        if (!isInfoRequestRuleConfigured(imRule)) {
          imRule = await this.$store.dispatch('accruals/loadImRule', {
            projectId: this.projectId,
          });
        }
        if (!isInfoRequestRuleConfigured(imRule)) {
          this.$store.commit(
            'sbError',
            'Save Info Request rules in Configuration before re-running.',
            { root: true },
          );
          return;
        }

        const result = await this.$store.dispatch('accruals/startImSelection', {
          projectId: this.projectId,
          ruleVersion: imRule.version,
        });
        this.rerunRulesDialogOpen = false;
        if (!result?.skipped) {
          await this.pollImSelectionUntilSettled(result?.selection_run_id);
        } else {
          await this.pollImSelectionUntilSettled();
        }
        await this.fetchEstimates();
      } catch (error) {
        this.reportWorkbenchError(error, 'Re-running rules failed. Try again.');
      } finally {
        this.rerunRulesLoading = false;
      }
    },
    async onPrepareSchedule() {
      console.log('[AccrualsEstimates]:', { action: 'prepare-schedule' });
      if (!this.canReviewEstimates) {
        this.$store.commit('sbError', 'Only the cycle Preparer can prepare the schedule.', { root: true });
        return;
      }
      this.prepareScheduleLoading = true;
      try {
        const data = await this.$store.dispatch('accruals/prepareSchedule', {
          projectId: this.projectId,
        });
        this.applyScheduleData(data);
        this.activeTab = 'schedule';
      } catch (error) {
        this.reportWorkbenchError(error, 'Preparing the schedule failed. Try again.');
      } finally {
        this.prepareScheduleLoading = false;
      }
    },
    onDownloadSchedule() {
      console.log('[AccrualsEstimates]:', { action: 'download-schedule', count: this.filteredScheduleRows.length });
      if (!this.filteredScheduleRows.length) return;
      downloadScheduleCsv(this.filteredScheduleRows, `accrual-schedule-${this.projectId}.csv`);
    },
    onScheduleColumnFilter(filters) {
      console.log('[AccrualsEstimates]:', { action: 'schedule-column-filter', filters });
      this.scheduleColumnFilters = filters || {};
      this.schedulePaginationPage = 1;
      this.expandedScheduleIds = [];
      this.closeScheduleDetail();
    },
    onClearScheduleColumnFilterState() {
      this.onScheduleColumnFilter({});
    },
    onScheduleMenuSelect(item) {
      console.log('[AccrualsEstimates]:', { action: 'schedule-menu', item: item?.id });
      if (item?.id === 'preview-export-je') {
        this.$store.commit('sbInfo', 'Preview & Export JE is not wired yet.', { root: true });
      }
    },
    onHeaderMenuSelect(item) {
      console.log('[AccrualsEstimates]:', { action: 'header-menu', item: item?.id });
      if (item?.id === 'configuration') {
        this.$nova.gotoLink({ path: ACCRUALS_ROUTES.configuration(this.projectId) });
        return;
      }
      if (item?.id === 'data') {
        this.$nova.gotoLink({ path: ACCRUALS_ROUTES.data(this.projectId) });
        return;
      }
      if (item?.id === 'info-requests') {
        const cid = this.$route.query.cid || this.$store.state.client;
        this.$router.push({
          path: `/financialclose/project/informationrequests/${this.projectId}`,
          query: cid ? { cid } : {},
        }).catch(() => {});
      }
    },
    onEstimatesStatusSelect(stageKey) {
      this.activeFilter = stageKey;
      this.locationFilters = {};
      this.hasActiveTableFilter = false;
      this.paginationPage = 1;
    },
    onColumnFilter(filters) {
      console.log('[AccrualsEstimates]:', { action: 'column-filter', filters });
      this.locationFilters = filters || {};
      this.paginationPage = 1;
      this.selectedIds = [];
      this.expandedGroupIds = [];
      this.closeDetailPanel();
    },
    onFilterActiveChange(isActive) {
      this.hasActiveTableFilter = !!isActive;
    },
    onClearColumnFilterState() {
      this.hasActiveTableFilter = false;
      this.onColumnFilter({});
    },
    onClearColumnFilter() {
      console.log('[AccrualsEstimates]:', { action: 'clear-column-filter' });
      if (this.$refs.estimatesTable?.clearColumnFilters) {
        this.$refs.estimatesTable.clearColumnFilters();
        return;
      }
      this.onClearColumnFilterState();
    },
    onBulkGlUpload() {
      this.onToolbarMenuSelect({ id: 'bulk-gl-upload' });
    },
    onAddAccruals() {
      console.log('[AccrualsEstimates]:', { action: 'add-accruals' });
      this.$store.commit('sbInfo', 'Add Accruals is not wired yet.', { root: true });
    },
    async onToolbarMenuSelect(item) {
      console.log('[AccrualsEstimates]:', { action: 'toolbar-menu', item: item?.id });
      if (item?.id === 'prepare-schedule') {
        await this.onPrepareSchedule();
        return;
      }
      if (item?.id === 'rerun-noa') {
        await this.onReRunEstimate();
        return;
      }
      if (item?.id === 'rerun-rules') {
        this.rerunDialogMode = 'rules';
        this.rerunRulesDialogOpen = true;
        return;
      }
      if (item?.id === 'bulk-gl-upload') {
        await this.onPrepareSchedule();
        return;
      }
      if (item?.id === 'sync-info-request') {
        this.$store.commit('sbInfo', 'Sync Info request is not wired yet.', { root: true });
      }
    },
    openInformationRequest(requestId) {
      const cid = this.$route.query.cid || this.$store.state.client;
      this.$router.push({
        path: `/financialclose/project/informationrequests/${this.projectId}/request/${requestId}`,
        query: cid ? { cid } : {},
      }).catch(() => {});
    },
    onInfoRequestAction(item) {
      console.log('[AccrualsEstimates]:', { action: 'info-request', row: item });
      if (item?.informationRequestId) {
        this.openInformationRequest(item.informationRequestId);
        return;
      }
      this.$store.commit('sbInfo', 'No information request linked to this estimate.', { root: true });
    },
    estimateReviewItem(row, option, customAmount) {
      return {
        estimate_id: row?.estimateId || row?.raw?._id || row?._id,
        line_key: row?.lineKey || null,
        amount_source: option || 'rule',
        revised_amount: customAmount || null,
      };
    },
    reportWorkbenchError(error, fallbackMessage) {
      this.statusUpdating = false;
      const message = error?.response?.data?.message
        || error?.message
        || fallbackMessage
        || 'Something went wrong. Try again.';
      console.log('[AccrualsEstimates]:', { action: 'error', message });
      if (!error?.__accrualsReported) {
        this.$store.commit('sbError', message, { root: true });
      }
      return message;
    },
    lineTargetStatus(row) {
      return workbenchColumnStatusKey(row);
    },
    buildSubmitItems(rows, group) {
      const groupSource = (group && this.sourceByGroupId[group._id])
        || (group ? defaultEstimateSource(group) : ACCRUALS_ESTIMATE_AMOUNT_OPTION.RULE);
      const items = [];
      const invalidRefs = [];
      (rows || []).forEach((row) => {
        if (!row || isVendorLevelEstimateRow(row)) return;
        const status = this.lineTargetStatus(row);
        if (status !== ACCRUALS_ESTIMATE_STATUS.PENDING_SUBMISSION
          && status !== ACCRUALS_ESTIMATE_STATUS.REJECTED) {
          return;
        }
        const option = defaultExpandLineSource(row, groupSource);
        let revisedAmount = null;
        if (option === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) {
          revisedAmount = expandLineActiveAmount(row, option, null);
          const error = validateCustomAmount(revisedAmount);
          if (error) {
            invalidRefs.push(estimateRefLabel(row));
            return;
          }
        } else {
          revisedAmount = expandLineActiveAmount(row, option, null);
        }
        items.push({
          estimate_id: row.estimateId || row.raw?._id || row._id,
          line_key: row.lineKey || null,
          amount_source: option,
          revised_amount: revisedAmount,
          note: '',
        });
      });
      return { items, invalidRefs };
    },
    buildDecisionItems(rows, note = '') {
      const trimmed = String(note || '').trim();
      return (rows || [])
        .filter((row) => row && !isVendorLevelEstimateRow(row)
          && (row.estimateId || row._id))
        .map((row) => ({
          estimate_id: row.estimateId || row.raw?._id || row._id,
          line_key: row.lineKey || null,
          ...(trimmed ? { note: trimmed } : {}),
        }));
    },
    async submitEstimateLines(rows, group, { note = '', option = '', customAmount = null, debitGl = '', creditGl = '' } = {}) {
      let items = [];
      if (option) {
        const row = rows;
        const amount = option === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM
          ? parseEstimateMoney(customAmount)
          : expandLineActiveAmount(row, option, null);
        if (option === ACCRUALS_ESTIMATE_AMOUNT_OPTION.CUSTOM) {
          const error = validateCustomAmount(customAmount);
          if (error) {
            this.$store.commit('sbError', error, { root: true });
            console.log('[AccrualsEstimates]:', { action: 'submit-validation-failed', rowId: row?._id });
            return null;
          }
        }
        items = [{
          estimate_id: row?.estimateId || row?.raw?._id || row?._id,
          line_key: row?.lineKey || null,
          amount_source: option,
          revised_amount: amount,
          debit_gl: debitGl,
          credit_gl: creditGl,
          note: note ? String(note) : '',
        }];
      } else {
        const built = this.buildSubmitItems(rows, group);
        if (built.invalidRefs.length) {
          const message = `${ACCRUALS_ESTIMATE_ERRORS.CUSTOM_AMOUNT_INVALID} (${built.invalidRefs.join(', ')})`;
          this.$store.commit('sbError', message, { root: true });
          console.log('[AccrualsEstimates]:', { action: 'submit-validation-failed', refs: built.invalidRefs });
          return null;
        }
        items = built.items.map((item) => ({ ...item, note: note ? String(note) : item.note }));
      }
      if (!items.length) {
        this.$store.commit('sbInfo', 'No lines waiting for submission in this selection.', { root: true });
        return null;
      }
      this.beginStatusRefresh();
      const data = await this.$store.dispatch('accruals/submitEstimates', {
        projectId: this.projectId,
        items,
      });
      return this.finishStatusChange(data);
    },
    async submitStructuredItems(entries) {
      const items = (entries || []).map((entry) => {
        const row = entry.row || {};
        return {
          estimate_id: row.estimateId || row.raw?._id || row._id,
          line_key: row.lineKey || null,
          amount_source: entry.source,
          revised_amount: entry.amount,
          debit_gl: entry.debitGl,
          credit_gl: entry.creditGl,
          note: entry.note ? String(entry.note) : '',
        };
      }).filter((item) => item.estimate_id && item.amount_source);
      if (!items.length) {
        this.$store.commit('sbInfo', 'No lines waiting for submission in this selection.', { root: true });
        return null;
      }
      console.log('[AccrualsEstimates]:', { action: 'submit-structured', count: items.length });
      this.beginStatusRefresh();
      const data = await this.$store.dispatch('accruals/submitEstimates', {
        projectId: this.projectId,
        items,
      });
      return this.finishStatusChange(data);
    },
    async approveEstimateLines(rows, note = '') {
      const items = this.buildDecisionItems(rows, note);
      if (!items.length) {
        this.$store.commit('sbInfo', 'No lines waiting for approval in this selection.', { root: true });
        return null;
      }
      this.beginStatusRefresh();
      const data = await this.$store.dispatch('accruals/approveAllEstimates', {
        projectId: this.projectId,
        items,
      });
      return this.finishStatusChange(data);
    },
    async rejectEstimateLines(rows, reason) {
      const reasonError = validateRejectReason(reason);
      if (reasonError) {
        this.$store.commit('sbError', reasonError, { root: true });
        return null;
      }
      const items = this.buildDecisionItems(rows);
      if (!items.length) {
        this.$store.commit('sbInfo', 'No lines waiting for decision in this selection.', { root: true });
        return null;
      }
      this.beginStatusRefresh();
      const data = await this.$store.dispatch('accruals/rejectEstimates', {
        projectId: this.projectId,
        items,
        reason: String(reason).trim(),
      });
      return this.finishStatusChange(data);
    },
    async excludeEstimateLines(rows, reason = '') {
      const items = this.buildDecisionItems(rows);
      if (!items.length) {
        this.$store.commit('sbInfo', 'No excludable lines in this selection.', { root: true });
        return null;
      }
      this.beginStatusRefresh();
      const data = await this.$store.dispatch('accruals/excludeEstimates', {
        projectId: this.projectId,
        items,
        reason: reason ? String(reason).trim() : undefined,
      });
      return this.finishStatusChange(data);
    },
    async includeEstimateLines(rows, reason = '') {
      const items = this.buildDecisionItems(rows);
      if (!items.length) {
        this.$store.commit('sbInfo', 'No excluded lines in this selection.', { root: true });
        return null;
      }
      this.beginStatusRefresh();
      const data = await this.$store.dispatch('accruals/includeEstimates', {
        projectId: this.projectId,
        items,
        reason: reason ? String(reason).trim() : undefined,
      });
      return this.finishStatusChange(data);
    },
    async requestResubmissionLines(rows, reason) {
      const reasonError = validateRejectReason(reason);
      if (reasonError) {
        this.$store.commit('sbError', reasonError, { root: true });
        return null;
      }
      const items = this.buildDecisionItems(rows);
      if (!items.length) {
        this.$store.commit('sbInfo', 'No approved lines in this selection.', { root: true });
        return null;
      }
      this.beginStatusRefresh();
      const data = await this.$store.dispatch('accruals/requestEstimateResubmission', {
        projectId: this.projectId,
        items,
        reason: String(reason).trim(),
      });
      return this.finishStatusChange(data);
    },
    decisionRows(payload) {
      if (Array.isArray(payload?.rows) && payload.rows.length) return payload.rows.filter(Boolean);
      if (Array.isArray(payload?.items) && payload.items.length) {
        return payload.items.map((item) => item.row).filter(Boolean);
      }
      return [payload?.row || this.selectedRow].filter(Boolean);
    },
    async onApproveEstimate(payload) {
      const rows = this.decisionRows(payload).filter((row) => (
        this.lineTargetStatus(row) === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
      ));
      console.log('[AccrualsEstimates]:', { action: 'approve-estimate', count: rows.length });
      if (!this.canApproveEstimates) {
        this.$store.commit('sbError', 'Only the cycle Approver can approve estimates.', { root: true });
        return;
      }
      const key = rows[0]?._id || payload?.group?._id || null;
      this.confirmingGroupId = key;
      try {
        await this.approveEstimateLines(rows, payload?.note || '');
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.APPROVE_FAILED);
      } finally {
        this.confirmingGroupId = null;
      }
    },
    async onUnlockEstimate(payload) {
      console.log('[AccrualsEstimates]:', { action: 'unlock-estimate', rowId: payload?.row?._id });
      if (!this.canUnlockEstimates) {
        this.$store.commit('sbError', 'Only the cycle Manager can unlock estimates.', { root: true });
        return;
      }
      const row = payload?.row || this.selectedRow;
      if (!row) return;
      try {
        await this.$store.dispatch('accruals/unlockEstimates', {
          projectId: this.projectId,
          items: [{
            estimate_id: row.estimateId || row.raw?._id || row._id,
            line_key: row.lineKey || null,
          }],
        });
        await this.fetchEstimates();
        this.rebindSelectedRow();
      } catch (error) {
        this.reportWorkbenchError(error, 'Could not unlock the estimate. Try again.');
      }
    },
    async onExcludeFromCycle(payload) {
      const row = payload?.row || this.selectedRow;
      console.log('[AccrualsEstimates]:', { action: 'exclude-from-cycle', rowId: row?._id });
      if (!this.canReviewEstimates && !this.isReviewerRole) {
        this.$store.commit('sbError', 'Only the cycle Preparer can exclude estimates.', { root: true });
        return;
      }
      if (row && this.lineTargetStatus(row) === ACCRUALS_ESTIMATE_STATUS.APPROVED) {
        this.$store.commit('sbError', 'Approved lines cannot be excluded.', { root: true });
        return;
      }
      if (!row) {
        this.$store.commit('sbInfo', 'Select an estimate line to exclude.', { root: true });
        return;
      }
      try {
        await this.excludeEstimateLines([row], payload?.reason || '');
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.EXCLUDE_FAILED);
      }
    },
    async onIncludeInCycle(payload) {
      const row = payload?.row || this.selectedRow;
      console.log('[AccrualsEstimates]:', { action: 'include-in-cycle', rowId: row?._id });
      if (!this.canReviewEstimates) {
        this.$store.commit('sbError', 'Only the cycle Preparer can include estimates.', { root: true });
        return;
      }
      if (!row) {
        this.$store.commit('sbInfo', 'Select an excluded estimate line to include.', { root: true });
        return;
      }
      try {
        await this.includeEstimateLines([row], payload?.reason || '');
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.INCLUDE_FAILED);
      }
    },
    onViewInfoRequest() {
      console.log('[AccrualsEstimates]:', { action: 'view-info-request', rowId: this.selectedRow?._id });
      const irId = this.selectedRow?.informationRequestId;
      if (irId) {
        this.openInformationRequest(irId);
        return;
      }
      this.$store.commit('sbInfo', 'No information request linked to this estimate.', { root: true });
    },
    onClearBulkSelection() {
      this.selectedIds = [];
      this.closeBulkApproveDialog();
    },
    selectedPendingApprovalEntries() {
      const selectedGroups = (this.vendorGroups || []).filter((row) => (
        this.selectedIds.includes(row._id)
      ));
      return selectedGroups.flatMap((group) => (group.children || [])
        .filter((row) => (
          row && !isVendorLevelEstimateRow(row)
          && this.lineTargetStatus(row) === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
          && this.rowAllowsApprove(row)
        ))
        .map((row) => ({ row, group })));
    },
    onOpenBulkApproveDialog() {
      if (!this.canApproveEstimates) {
        this.$store.commit('sbError', 'Only the cycle Approver can approve estimates.', { root: true });
        return;
      }
      const entries = this.selectedPendingApprovalEntries();
      if (!entries.length) {
        this.$store.commit('sbInfo', 'No lines waiting for approval in this selection.', { root: true });
        return;
      }
      this.bulkApproveRows = entries;
      this.bulkApproveNote = '';
      this.bulkApproveOpen = true;
      console.log('[AccrualsEstimates]:', {
        action: 'open-bulk-approve',
        count: entries.length,
      });
    },
    closeBulkApproveDialog() {
      this.bulkApproveOpen = false;
      this.bulkApproveRows = [];
      this.bulkApproveNote = '';
    },
    async onConfirmBulkApprove() {
      const rows = (this.bulkApproveRows || []).map((entry) => entry.row || entry).filter(Boolean);
      if (!rows.length) {
        this.$store.commit('sbInfo', 'No lines waiting for approval in this selection.', { root: true });
        this.closeBulkApproveDialog();
        return;
      }
      this.bulkActionLoading = true;
      try {
        await this.approveEstimateLines(rows, this.bulkApproveNote);
        this.selectedIds = [];
        this.closeBulkApproveDialog();
      } catch (error) {
        this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.APPROVE_FAILED);
      } finally {
        this.bulkActionLoading = false;
      }
    },
    onBulkAction(actionId) {
      const selectedGroups = (this.vendorGroups || []).filter((row) => (
        this.selectedIds.includes(row._id)
      ));
      console.log('[AccrualsEstimates]:', {
        action: 'bulk-action',
        actionId,
        selectedCount: selectedGroups.length,
        rowIds: selectedGroups.map((row) => row._id),
      });
      if (!selectedGroups.length) return;

      if (actionId === 'confirm-selected' || actionId === 'submit-for-approval') {
        if (!this.canReviewEstimates) {
          this.$store.commit('sbError', 'Only the cycle Preparer can submit estimates.', { root: true });
          return;
        }
        const built = selectedGroups.map((group) => this.buildSubmitItems(
          (group.children || []).filter((row) => row && !row.locked),
          group,
        ));
        const invalidRefs = built.flatMap((result) => result.invalidRefs);
        if (invalidRefs.length) {
          const message = `${ACCRUALS_ESTIMATE_ERRORS.CUSTOM_AMOUNT_INVALID} (${invalidRefs.join(', ')})`;
          this.$store.commit('sbError', message, { root: true });
          console.log('[AccrualsEstimates]:', { action: 'bulk-submit-validation-failed', refs: invalidRefs });
          return;
        }
        const items = built.flatMap((result) => result.items);
        if (!items.length) {
          this.$store.commit('sbInfo', 'No lines waiting for submission in this selection.', { root: true });
          return;
        }
        this.bulkActionLoading = true;
        this.$store.dispatch('accruals/submitEstimates', {
          projectId: this.projectId,
          items,
        }).then(() => {
          this.selectedIds = [];
          return this.fetchEstimates();
        }).catch((error) => {
          this.reportWorkbenchError(error, ACCRUALS_ESTIMATE_ERRORS.SUBMIT_FAILED);
        }).finally(() => {
          this.bulkActionLoading = false;
        });
        return;
      }
      if (actionId === 'approve-all') {
        this.onOpenBulkApproveDialog();
        return;
      }
      if (actionId === 'reject') {
        if (!this.canApproveEstimates) {
          this.$store.commit('sbError', 'Only the cycle Approver can reject estimates.', { root: true });
          return;
        }
        const rows = selectedGroups.flatMap((group) => (group.children || []).filter((row) => (
          row && !isVendorLevelEstimateRow(row)
          && this.lineTargetStatus(row) === ACCRUALS_ESTIMATE_STATUS.PENDING_APPROVAL
        )));
        if (!rows.length) {
          this.$store.commit('sbInfo', 'No lines waiting for approval in this selection.', { root: true });
          return;
        }
        this.bulkReasonMode = 'reject';
        this.bulkReasonRows = rows;
        this.bulkReasonText = '';
        this.bulkReasonError = '';
        this.bulkReasonOpen = true;
        console.log('[AccrualsEstimates]:', {
          action: 'open-bulk-reject',
          refs: rows.map(estimateRefLabel),
        });
        return;
      }
      if (actionId === 'exclude') {
        if (!this.canReviewEstimates && !this.isReviewerRole) {
          this.$store.commit('sbError', 'Only the cycle Preparer can exclude estimates.', { root: true });
          return;
        }
        const rows = selectedGroups.flatMap((group) => (group.children || []).filter((row) => (
          row && !isVendorLevelEstimateRow(row)
          && this.lineTargetStatus(row) !== ACCRUALS_ESTIMATE_STATUS.EXCLUDED
          && this.lineTargetStatus(row) !== ACCRUALS_ESTIMATE_STATUS.APPROVED
        )));
        if (!rows.length) {
          this.$store.commit('sbInfo', 'No excludable lines in this selection.', { root: true });
          return;
        }
        this.bulkReasonMode = 'exclude';
        this.bulkReasonRows = rows;
        this.bulkReasonText = '';
        this.bulkReasonError = '';
        this.bulkReasonOpen = true;
        return;
      }
      if (actionId === 'include') {
        if (!this.canReviewEstimates) {
          this.$store.commit('sbError', 'Only the cycle Preparer can include estimates.', { root: true });
          return;
        }
        const rows = selectedGroups.flatMap((group) => (group.children || []).filter((row) => (
          row && !isVendorLevelEstimateRow(row)
          && this.lineTargetStatus(row) === ACCRUALS_ESTIMATE_STATUS.EXCLUDED
        )));
        if (!rows.length) {
          this.$store.commit('sbInfo', 'No excluded lines in this selection.', { root: true });
          return;
        }
        this.bulkReasonMode = 'include';
        this.bulkReasonRows = rows;
        this.bulkReasonText = '';
        this.bulkReasonError = '';
        this.bulkReasonOpen = true;
      }
    },
    runBulkDecision(actionId, rows, storeAction, fallbackMessage) {
      const items = this.buildDecisionItems(rows);
      if (!items.length) {
        this.$store.commit('sbInfo', 'No eligible lines in this selection.', { root: true });
        return;
      }
      this.bulkActionLoading = true;
      this.$store.dispatch(storeAction, {
        projectId: this.projectId,
        items,
      }).then(() => {
        this.selectedIds = [];
        return this.fetchEstimates();
      }).catch((error) => {
        this.reportWorkbenchError(error, fallbackMessage);
      }).finally(() => {
        this.bulkActionLoading = false;
      });
    },
    closeBulkReasonDialog() {
      this.bulkReasonOpen = false;
      this.bulkReasonMode = 'reject';
      this.bulkReasonRows = [];
      this.bulkReasonText = '';
      this.bulkReasonError = '';
    },
    async onConfirmBulkReject() {
      const error = validateRejectReason(this.bulkReasonText);
      this.bulkReasonError = error;
      if (error) {
        this.$store.commit('sbError', error, { root: true });
        console.log('[AccrualsEstimates]:', { action: 'bulk-reject-validation-failed' });
        return;
      }
      const reason = String(this.bulkReasonText).trim();
      const items = this.buildDecisionItems(this.bulkReasonRows);
      if (!items.length) {
        this.$store.commit('sbInfo', 'No eligible lines in this selection.', { root: true });
        this.closeBulkReasonDialog();
        return;
      }
      this.bulkActionLoading = true;
      this.beginStatusRefresh();
      const action = this.bulkReasonMode === 'exclude'
        ? 'accruals/excludeEstimates'
        : this.bulkReasonMode === 'include'
          ? 'accruals/includeEstimates'
          : 'accruals/rejectEstimates';
      const fallback = this.bulkReasonMode === 'exclude'
        ? ACCRUALS_ESTIMATE_ERRORS.EXCLUDE_FAILED
        : this.bulkReasonMode === 'include'
          ? ACCRUALS_ESTIMATE_ERRORS.INCLUDE_FAILED
          : ACCRUALS_ESTIMATE_ERRORS.REJECT_FAILED;
      try {
        const data = await this.$store.dispatch(action, {
          projectId: this.projectId,
          items,
          reason,
        });
        this.selectedIds = [];
        this.closeBulkReasonDialog();
        await this.finishStatusChange(data);
      } catch (err) {
        this.reportWorkbenchError(err, fallback);
      } finally {
        this.bulkActionLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './AccrualsEstimates.scss';
</style>
